import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Analytics helper – pushes to dataLayer for gtag / Google tags
export const analytics = {
  track: (eventName, properties = {}) => {
    if (typeof window === 'undefined') return;
    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...properties });
    }
    console.log(`[Analytics] ${eventName}`, properties);
  },
  
  // Specific event trackers
  trackLocationSearch: (query) => {
    analytics.track('location_search', { search_query: query });
  },
  
  trackLocationSelect: (locationId, locationName) => {
    analytics.track('location_select', { location_id: locationId, location_name: locationName });
  },
  
  trackSignupClick: (locationId, locationName) => {
    analytics.track('signup_click', { location_id: locationId, location_name: locationName });
  },
  
  trackCancelSubmit: (locationId) => {
    analytics.track('cancel_submit', { location_id: locationId });
  },
  
  trackFeedbackSubmit: (category) => {
    analytics.track('feedback_submit', { category });
  },
  
  trackContactSubmit: () => {
    analytics.track('contact_submit', {});
  },

  trackFreePassSubmit: (locationId, locationName = '') => {
    analytics.track('free_pass_conversion', { location_id: locationId, location_name: locationName });
  },
};

// API base URL (fallback to same-origin /api when env not set)
const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_URL = base ? `${base}/api` : "/api";

// Format phone number
export const formatPhone = (phone) => {
  if (!phone) return '';
  return phone;
};

// Get today's day name
export const getTodaysDayName = () => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[new Date().getDay()];
};

// Get hours for today
export const getTodaysHours = (hours) => {
  if (!hours || !Array.isArray(hours)) return 'Hours not available';
  
  const today = getTodaysDayName();
  const todayHours = hours.find(h => h.day === today);
  
  if (!todayHours) return 'Hours not available';
  if (todayHours.is_closed) return 'Closed';
  if (todayHours.open === '24 Hours') return '24 Hours';
  
  return `${todayHours.open} - ${todayHours.close}`;
};

// Calculate distance text
export const formatDistance = (distance) => {
  if (distance === undefined || distance === null) return '';
  if (distance < 0.1) return '< 0.1 mi';
  return `${distance.toFixed(1)} mi`;
};

// US zip pattern (5 digits or 5+4)
const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;

// Geocode a place (zip, city, address) to lat/lng via OpenStreetMap Nominatim (no API key)
export async function geocodePlace(query) {
  if (!query || typeof query !== "string" || !query.trim()) return null;
  let q = query.trim();
  // Nominatim often fails on bare US zips; appending ", USA" improves results
  if (US_ZIP_REGEX.test(q)) {
    q = `${q}, USA`;
  }
  const encoded = encodeURIComponent(q);
  const url = `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=1`;
  const res = await fetch(url, {
    headers: { Accept: "application/json", "User-Agent": "JerseyGirlsWebsite/1.0" },
  });
  if (!res.ok) return null;
  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  const { lat, lon } = data[0];
  const latNum = parseFloat(lat);
  const lonNum = parseFloat(lon);
  if (Number.isNaN(latNum) || Number.isNaN(lonNum)) return null;
  return { lat: latNum, lng: lonNum };
}

export function isUsZipCode(query) {
  return query && typeof query === "string" && US_ZIP_REGEX.test(query.trim());
}

// Add cache-busting query parameter to image URLs
// Uses build timestamp or commit SHA so images refresh on each deployment
export function addCacheBust(url) {
  if (!url || typeof url !== 'string') return url;
  // Skip external URLs (Unsplash, etc.)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const urlObj = new URL(url);
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      if (urlObj.origin !== currentOrigin && urlObj.hostname !== 'localhost') {
        return url; // External URL, don't modify
      }
    } catch (e) {
      return url; // Invalid URL, return as-is
    }
  }
  // Add version for cache busting
  // Cloudflare Pages sets CF_PAGES_COMMIT_SHA on each deploy
  // Fallback to build time or current timestamp
  const separator = url.includes('?') ? '&' : '?';
  const buildVersion = 
    (typeof process !== 'undefined' && process.env.REACT_APP_BUILD_TIME) ||
    (typeof process !== 'undefined' && process.env.CF_PAGES_COMMIT_SHA) ||
    Date.now();
  return `${url}${separator}_v=${buildVersion}`;
}
